import React, { ReactElement, useState, useEffect } from 'react';
import { Collapse, notification, Modal, Row, Col, Popover } from 'antd';
import styles from './DbUsers.module.less';
import AppEmpty from '../AppEmpty';
import { Link, useHistory, useParams } from 'react-router-dom';
import useDbUsers from '../../core/hooks/useDbUsers';
import useDatabases from '../../core/hooks/useDatabases';
import AppTable from '../AppTable';
import { InfoCircleOutlined, StopOutlined } from '@ant-design/icons';
import CopyToClipboardText from '../CopyToClipboardText';
import AddDbUser from './AddDbUser';
import AppConfirmDialog from '../AppConfirmDialog';
import DbUserService from '../../services/DbUserService';
import AppLoading from '../AppLoading';
import Deployments from '../../types/Deployments';
import ConnectionAssistant from './ConnectionAssistant';
import DbUser, { DEFAULT_DATABASE_USER } from '../../types/DbUser';
import { Tag } from 'antd';
import Deployment from '../../types/Deployment';
import useDataStore from '../../core/hooks/useDataStore';
import CcxSectionHeader from '../ccx/common/CcxSectionHeader';
import CcxIconCheckCircleTwoTone from '../ccx/icons/CcxIconCheckCircleTwoTone';
import CcxIconCloseCircleTwoTone from '../ccx/icons/CcxIconCloseCircleTwoTone';
import CcxIconInfoCircleTwoTone from '../ccx/icons/CcxIconInfoCircleTwoTone';
import CcxIconDeleteOutlined from '../ccx/icons/CcxIconDeleteOutlined';
import { Tooltip } from 'antd';
import AppCodeCard from '../AppCodeCard';
import TypographyText from '../../tmp/TypographyText';
import CcxCustomTooltip from '../ccx/common/CcxCustomTooltip';
import './DbUser.less';
interface UrlProps {
    dataStoreUuid: string;
    projectUuid: string;
}

type DbUsersProps = {
    deployments?: Deployments;
    deployment?: Deployment;
    onUpdate?: Function;
};

function DbUsers({ deployments, onUpdate }: DbUsersProps): ReactElement {
    const { Panel } = Collapse;
    let { dataStoreUuid, projectUuid } = useParams<UrlProps>();
    const { dataStore: deployment } = useDataStore(dataStoreUuid);
    const currentDeployment = deployments?.getByUuid(dataStoreUuid);
    const {
        dbUsers,
        loading: dbUsersLoading,
        refresh: dbUsersRefresh,
    } = useDbUsers(dataStoreUuid); // @TODO: Add refresh here
    const { databases } = useDatabases(dataStoreUuid);
    const [deleting, setDeleting] = useState<boolean>(false);
    const [dbUsersData, setDbUsersData] = useState<any>([]);
    const [hosts, setHosts] = useState<any>(undefined);
    const [dbService, setDbService] = useState('');
    const [usersHosts, setUsersHosts] = useState<string[]>([]);

    let history = useHistory();

    useEffect(() => {
        if (deployment) {
            setHosts(deployment.getHosts());
        }
    }, [deployment]);

    useEffect(() => {
        if (currentDeployment?.isRedis()) {
            history.goBack();
        }
    }, [currentDeployment]);

    useEffect(() => {
        setDbService(currentDeployment?.getServiceName() || '');

        if (dbUsers && dbUsers.accounts) {
            const data = dbUsers.accounts.map((u: DbUser) => {
                return {
                    ...u,
                    uniqueGrants: u.getUniqueGrants(),
                };
            });
            const sortedUsers = [...data].sort((a, b) =>
                a.username.localeCompare(b.username)
            );
            setDbUsersData(sortedUsers);
        }
    }, [dbUsers, currentDeployment]);

    useEffect(() => {
        if (dbUsers && dbUsers.accounts) {
            setUsersHosts(
                dbUsers.accounts?.map((u: DbUser) => {
                    return u.username;
                })
            );
        }
    }, [dbUsers]);

    const usersColumns = [
        {
            title: 'Account',
            key: 'user',
            width: 260,
            render: (text: string, record: any) => {
                return currentDeployment?.isMariaDb() ||
                    currentDeployment?.isPercona() ? (
                    <CopyToClipboardText text={record.username} />
                ) : (
                    <CopyToClipboardText text={`${record.username}`} />
                );
            },
        },
        {
            title: 'Privileges',
            key: 'privileges',
            ellipsis: true,
            render: (text: string, record: any) => {
                const privileges = record?.grants
                    ?.split(';')
                    ?.map((privilege: string) => {
                        if (privilege.indexOf(':') === -1) {
                            return {
                                db: '*.*',
                                privileges: privilege.split(',').sort(),
                            };
                        }
                        const [database, privileges] = privilege.split(':');
                        return {
                            db: database,
                            privileges: privileges.split(',').sort(),
                        };
                    });
                return (
                    <Row gutter={[16, 16]}>
                        {privileges?.map((priv: any) => {
                            return (
                                <Col
                                    span={8}
                                    className={styles.DbUsersSegmentedData}
                                >
                                    <div
                                        className={
                                            styles.DbUsersSegmentedFirstItem
                                        }
                                    >
                                        {priv.db}
                                    </div>
                                    <Popover
                                        content={
                                            <Row>
                                                {priv.privileges?.map(
                                                    (item: string) => (
                                                        <Col
                                                            span={8}
                                                            className={
                                                                styles.DbUserPopoverCol
                                                            }
                                                        >
                                                            <ul>
                                                                <li key={item}>
                                                                    {item.trim()}
                                                                </li>
                                                            </ul>
                                                        </Col>
                                                    )
                                                )}
                                            </Row>
                                        }
                                        title={
                                            <span
                                                className={
                                                    styles.DBUserPopoverTitle
                                                }
                                            >
                                                {priv.db === '*.*'
                                                    ? 'Global Privileges'
                                                    : 'Privileges'}
                                            </span>
                                        }
                                        destroyTooltipOnHide={true}
                                        arrowPointAtCenter={false}
                                        overlayInnerStyle={{
                                            padding: 0,
                                            borderRadius: 16,
                                        }}
                                        overlayStyle={{
                                            maxWidth: 500,
                                            minWidth: 400,
                                        }}
                                        overlayClassName="DbUserPopover"
                                    >
                                        <div
                                            className={
                                                styles.DbUsersSegmentedSecondItem
                                            }
                                        >
                                            {priv.privileges.join(',')}
                                        </div>
                                    </Popover>
                                </Col>
                            );
                        })}
                    </Row>
                );
            },
        },
        {
            title: <TypographyText nowrap={true}>Actions</TypographyText>,
            key: 'actions',
            width: 90,
            render: (text: string, record: any) => {
                const onConfirmDelete = () => {
                    return new Promise(async (resolve, reject) => {
                        setDeleting(true);

                        notification.open({
                            message: 'Remove account',
                            description: 'The account will be removed soon.',
                            icon: <CcxIconInfoCircleTwoTone />,
                        });

                        Modal.destroyAll();

                        try {
                            const data = {
                                uuid: dataStoreUuid,
                                dbUsername: record.username,
                                dbHost: record.hostAllow,
                            };
                            await DbUserService.deleteUser(data);

                            dbUsersRefresh && (await dbUsersRefresh());
                            onUpdate && onUpdate();

                            notification.open({
                                message: 'Remove Account',
                                description: 'Account successfully removed',
                                icon: (
                                    <CcxIconCheckCircleTwoTone twoToneColor="#52c41a" />
                                ),
                            });

                            setDeleting(false);
                            resolve(null);
                        } catch (e) {
                            notification.open({
                                message: 'Remove Account',
                                description: `There was an error removing the account. ${e}`,
                                icon: (
                                    <CcxIconCloseCircleTwoTone twoToneColor="#eb2f96" />
                                ),
                            });

                            console.error(e);

                            setDeleting(false);
                            reject();
                        }
                    }).catch(() => console.log('Oops errors!'));
                };

                return (
                    <div className={styles.DbUsersOptionsColumn}>
                        {record.username === DEFAULT_DATABASE_USER ? (
                            <Tooltip
                                placement="top"
                                title="Default user cannot be deleted"
                            >
                                <StopOutlined className={styles.DbUsersStop} />
                            </Tooltip>
                        ) : (
                            <AppConfirmDialog
                                critical={true}
                                onOk={onConfirmDelete}
                                content="The account will be removed and will not be able to connect to the database."
                                actionIcon={<CcxIconDeleteOutlined />}
                            />
                        )}
                    </div>
                );
            },
        },
    ];

    return (
        <section className={styles.DbUsers}>
            <CcxSectionHeader>
                <>
                    {currentDeployment?.isMSSQL() ? undefined : (
                        <ConnectionAssistant
                            databases={databases?.databases}
                            users={usersHosts}
                            hosts={hosts}
                            service={dbService}
                        />
                    )}
                    {databases ? (
                        <AddDbUser
                            onSuccess={dbUsersRefresh}
                            uuid={dataStoreUuid}
                            databases={databases.databases}
                            currentDeployment={currentDeployment}
                        />
                    ) : null}
                </>
            </CcxSectionHeader>
            <h4 className={styles.DbUsersLabel}>Users</h4>
            {dbUsersLoading ? (
                <AppLoading />
            ) : dbUsers?.accounts.length ? (
                <AppTable
                    columns={usersColumns}
                    data={dbUsersData}
                    rowKey="uniqueKey"
                    expandable={false}
                    footer={() => (
                        <>
                            <InfoCircleOutlined /> Accounts are allowed to
                            connect from trusted sources controlled by the{' '}
                            <Link
                                to={`/projects/${projectUuid}/data-stores/${dataStoreUuid}/firewall`}
                            >
                                Firewall
                            </Link>
                        </>
                    )}
                    pagination={{
                        hideOnSinglePage: true,
                    }}
                />
            ) : (
                <AppEmpty message="There are no users added yet" />
            )}
        </section>
    );
}

export default DbUsers;
